import type {
  CreateLeaveRequestBody,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestStatus,
  LeaveType,
} from '@mervonn/shared-types';
import { apiList, apiPage, apiRequest } from '../../lib/api/client';

/** `POST /leave-requests/bulk-approve` (Stage 7 Phase 2D). */
export interface BulkApproveResult {
  approved: number;
  results: { request_id: string; status: 'approved' | 'failed'; error?: string }[];
}

/** `GET /leave-balances/department-rollup`. */
export interface DepartmentRollupRow {
  department_id: string | null;
  department_name: string;
  employees: number;
  entitled_days: number;
  used_days: number;
  remaining_days: number;
  avg_remaining: number | null;
  utilization_pct: number | null;
  status: 'healthy' | 'watch' | 'low';
}

export interface LeaveRequestsFilter {
  status?: LeaveRequestStatus;
  /** Approved and rejected. */
  decided?: 'true';
  /** The caller's own, whatever else their role can see. */
  mine?: 'true';
  branch_id?: string;
  leave_type_id?: string;
  /** Name or employee number. */
  search?: string;
  /** Leave overlapping `from`..`to` (`YYYY-MM-DD`). */
  from?: string;
  to?: string;
  /** Only leave starting on or after this date. */
  starts_from?: string;
}

export interface LeaveRequestsPageQuery extends LeaveRequestsFilter {
  page: number;
  per_page: number;
}

export const leaveApi = {
  /** One page of requests, newest first (earliest first when a date window is given). */
  requestsPage: (q: LeaveRequestsPageQuery) => apiList<LeaveRequest>('/leave-requests', { query: { ...q } }),

  // Each request still goes through the normal approve rules server-side; this
  // never overrides an insufficient balance (that stays a deliberate single action).
  bulkApprove: (ids: string[]) =>
    apiRequest<BulkApproveResult>('/leave-requests/bulk-approve', { method: 'POST', body: { request_ids: ids } }),

  departmentRollup: () =>
    apiPage<{ year: number; data: DepartmentRollupRow[] }>('/leave-balances/department-rollup'),

  create: (body: CreateLeaveRequestBody) =>
    apiRequest<LeaveRequest>('/leave-requests', { method: 'POST', body }),

  approve: (id: string, overrideInsufficientBalance = false) =>
    apiRequest<LeaveRequest>(`/leave-requests/${id}/approve`, {
      method: 'PATCH',
      body: { override_insufficient_balance: overrideInsufficientBalance },
    }),

  reject: (id: string, reason?: string) =>
    apiRequest<LeaveRequest>(`/leave-requests/${id}/reject`, {
      method: 'PATCH',
      body: { reason: reason || undefined },
    }),

  balances: () => apiRequest<LeaveBalance[]>('/leave-balances'),
  types: () => apiRequest<LeaveType[]>('/leave-types'),
};

export function leaveTypeName(types: LeaveType[] | undefined, id: string): string {
  return types?.find((t) => t.leave_type_id === id)?.name ?? 'Leave';
}
